import { IEmbed, IEmbedCreate, IEmbedGet } from '@opengov/embed-types';
import { IEmbedData, IEmbedV3 } from '@opengov/reporting-types';

import { baseEmbedServiceUrl } from './paths';
import { getServiceURL } from './transportWrapper';

const gatewayApiPrefix = '/api/gateway_service/v1';
const embedApiV3Prefix = '/api/embed_service/v3';
const embedApiV2Prefix = `/api/embed_crud_service/v2`;

const getEmbedServiceUrl = (hostUrl?: string) => getServiceURL(hostUrl || process.env.EMBED_SERVICE_HOST);

export const syncEmbedByReportIds = async (
  params: {
    ids: (number | string)[];
    messageId: string;
    clientOptions?: RequestInit;
  },
  hostUrl?: string
) => {
  const { ids, messageId, clientOptions = {} } = params;
  return getEmbedServiceUrl(hostUrl)
    .options(clientOptions, true)
    .url(`${gatewayApiPrefix}/sync`)
    .post({ data: { report_ids: ids }, message_id: messageId })
    .json()
    .then();
};

export const syncEmbedBySavedViewId = async (
  params: {
    savedViewId: string;
    clientOptions?: RequestInit;
  },
  hostUrl?: string
) => {
  const { savedViewId, clientOptions = {} } = params;
  return getEmbedServiceUrl(hostUrl)
    .options(clientOptions, true)
    .url(`${gatewayApiPrefix}/sync_saved_view`)
    .post({ savedViewId })
    .json()
    .then();
};

export const getEmbedV3ById = async (params: { embedId: string; clientOptions?: RequestInit }) => {
  const { embedId, clientOptions = {} } = params;
  return getEmbedServiceUrl().options(clientOptions, true).url(`${embedApiV3Prefix}/embeds/${embedId}`).get().json().then();
};

export const createEmbedV3 = async (params: {
  dataProviderId: string;
  creationParams: Object;
  clientOptions?: RequestInit;
}) => {
  const { dataProviderId, creationParams, clientOptions = {} } = params;
  return getEmbedServiceUrl()
    .options(clientOptions, true)
    .url(`${embedApiV3Prefix}/embeds`)
    .post({
      dataProviderId,
      creationParams
    })
    .json()
    .then()
    .catch();
};

export const deleteEmbedV3 = async (params: { embedId: string; clientOptions?: RequestInit }) => {
  const { embedId, clientOptions = {} } = params;
  return getEmbedServiceUrl()
    .options(clientOptions, true)
    .url(`${embedApiV3Prefix}/embeds/${embedId}`)
    .delete()
    .res(async (response) => {
      if (response.status === 200) {
        return Promise.resolve();
      }

      return Promise.reject(response);
    })
    .catch(async (e) => {
      return Promise.reject(e);
    });
};

export const updateEmbedV3 = async (
  params: {
    id: string;
    title?: string;
    description?: string;
    type?: string;
    savedViewId: string;
    shouldSync: boolean;
    reportId: string;
  },
  clientOptions?: RequestInit
): Promise<IEmbedV3> => {
  return getEmbedServiceUrl()
    .options(clientOptions, true)
    .url(`${embedApiV3Prefix}/embeds/${params.id}`)
    .json(params)
    .put()
    .json<IEmbedV3>()
    .catch(async (e) => {
      e.message = `updateEmbedV3: ${e.message}`;
      return Promise.reject(e);
    });
};

export const createNewEmbed = async (
  params: {
    title: string;
    description?: string;
    saved_view_id: string;
    report_version: string;
    should_sync: boolean;
    report_id: string;
    type?: string;
  },
  clientOptions?: RequestInit
): Promise<IEmbedCreate> => {
  return getEmbedServiceUrl()
    .options(clientOptions, true)
    .url(`${baseEmbedServiceUrl}/embeds`)
    .json(params)
    .post()
    .json<IEmbedCreate>()
    .catch(async (e) => {
      e.message = `createNewEmbed: ${e.message}`;
      return Promise.reject(e);
    });
};

export type AsyncEmbedBuildStatus = 'PENDING' | 'READY' | 'FAILED';

/**
 * Response of the async embed routes. Once READY, `embed` is the saved embed (`id`, `privateUrl`, `data`, …): the
 * same object the synchronous create route returns as `data`, so callers can use it in its place.
 */
export interface IAsyncEmbedBuild {
  status: number;
  data: {
    jobId: string;
    status: AsyncEmbedBuildStatus;
    embed?: IEmbedGet;
    error?: string;
  };
}

/**
 * Starts building an embed in the background and returns at once with a job id (HTTP 202), so a report whose
 * query outlasts the CDN timeout can still be embedded. Poll `getAsyncEmbedBuild` until it is READY or FAILED.
 */
export const createNewEmbedAsync = async (
  params: Parameters<typeof createNewEmbed>[0],
  clientOptions?: RequestInit
): Promise<IAsyncEmbedBuild> => {
  return getEmbedServiceUrl()
    .options(clientOptions, true)
    .url(`${baseEmbedServiceUrl}/embeds/async`)
    .json(params)
    .post()
    .json<IAsyncEmbedBuild>()
    .catch((error: Error) => {
      error.message = `createNewEmbedAsync: ${error.message}`;
      throw error;
    });
};

export const getAsyncEmbedBuild = async (
  params: { jobId: string },
  clientOptions?: RequestInit
): Promise<IAsyncEmbedBuild> => {
  return getEmbedServiceUrl()
    .options(clientOptions, true)
    .url(`${baseEmbedServiceUrl}/embeds/async/${encodeURIComponent(params.jobId)}`)
    .get()
    .json<IAsyncEmbedBuild>()
    .catch((error: Error) => {
      error.message = `getAsyncEmbedBuild: ${error.message}`;
      throw error;
    });
};

export const createPreviewEmbedV2 = async (
  params: {
    title: string;
    description?: string;
    saved_view_id: string;
    report_version: string;
    should_sync: boolean;
    report_id: string;
    type?: string;
  },
  clientOptions?: RequestInit
): Promise<IEmbedData<any>> => {
  return getEmbedServiceUrl()
    .options(clientOptions, true)
    .url(`${baseEmbedServiceUrl}/embeds/preview`)
    .json(params)
    .post()
    .json<IEmbedData<any>>()
    .catch(async (e) => {
      e.message = `createPreviewEmbedV2: ${e.message}`;
      return Promise.reject(e);
    });
};

export const createPreviewEmbedV3 = async (
  params: {
    title?: string;
    description?: string;
    type?: string;
    savedViewId: string;
    shouldSync: boolean;
    reportId: string;
  },
  clientOptions?: RequestInit
): Promise<IEmbedV3> => {
  return getEmbedServiceUrl()
    .options(clientOptions, true)
    .url(`${embedApiV3Prefix}/embeds/preview`)
    .json(params)
    .post()
    .json<IEmbedV3>()
    .catch(async (e) => {
      e.message = `createNewEmbedV3: ${e.message}`;
      return Promise.reject(e);
    });
};

export const createNewEmbedV3 = async (
  params: {
    title?: string;
    description?: string;
    type?: string;
    savedViewId: string;
    shouldSync: boolean;
    reportId: string;
  },
  clientOptions?: RequestInit
): Promise<IEmbedV3> => {
  return getEmbedServiceUrl()
    .options(clientOptions, true)
    .url(`${embedApiV3Prefix}/embeds`)
    .json(params)
    .post()
    .json<IEmbedV3>()
    .catch(async (e) => {
      e.message = `createNewEmbedV3: ${e.message}`;
      return Promise.reject(e);
    });
};

export const getReportDataByEmbedIds = async (
  params: { embedIds: string[] },
  clientOptions?: RequestInit
): Promise<IEmbed[]> =>
  getEmbedServiceUrl()
    .options(clientOptions, true)
    .url(`${embedApiV2Prefix}/reportEmbedsByEmbedId`)
    .post({ embedValues: params.embedIds, isServiceUser: true })
    .json<IEmbed[]>();
